import type { Ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { InfiniteData } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useShop } from "@app/vue/providers/ShopConnection";
import { reservationRequestKeys } from "../data/queryKeys";
import {
  invalidateAfterAcceptRequest,
  invalidateAfterRejectRequest,
} from "../data/invalidation";
import type {
  ReservationRequest,
  Reservation,
  ReservationDeclineReason,
} from "@lemmonhq/types";

interface PageData {
  data: ReservationRequest[];
  pagination: { limit: number; offset: number; hasNext: boolean };
}

export function useAcceptRequestMutation(options?: {
  onSuccess?: () => void;
}): {
  mutate: (input: {
    request_id: string;
    table_ids?: number[];
    duration_mins?: number;
    kitchen_note?: boolean;
    message?: string;
    hide_duration?: boolean;
  }) => Promise<{
    request: ReservationRequest;
    reservation: Reservation | null;
  }>;
  isPending: Ref<boolean>;
} {
  const trpc = useTrpc();
  const queryClient = useQueryClient();
  const shop = useShop();

  const mutation = useMutation({
    mutationFn: (input: {
      request_id: string;
      table_ids?: number[];
      duration_mins?: number;
      kitchen_note?: boolean;
      message?: string;
      hide_duration?: boolean;
    }) =>
      trpc.reservations.acceptRequest.mutate({
        request_id: input.request_id,
        table_ids: input.table_ids ?? [],
        duration_mins: input.duration_mins,
        kitchen_note: input.kitchen_note,
        message: input.message,
        hide_duration: input.hide_duration,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: reservationRequestKeys.lists(),
      });
      const snapshots = queryClient.getQueriesData<InfiniteData<PageData>>({
        queryKey: reservationRequestKeys.lists(),
      });
      queryClient.setQueriesData<InfiniteData<PageData>>(
        { queryKey: reservationRequestKeys.lists() },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  data: page.data.filter((r) => r.id !== input.request_id),
                })),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_err, _input, context) => {
      context?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSettled: async (_data, _err, input) => {
      await invalidateAfterAcceptRequest(queryClient, {
        requestId: input.request_id,
      });
    },
    onSuccess: () => {
      void shop.refreshData();
      options?.onSuccess?.();
    },
  });

  return {
    mutate: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending as Ref<boolean>,
  };
}

export function useRejectRequestMutation(options?: {
  onSuccess?: () => void;
}): {
  mutate: (input: {
    request_id: string;
    status: "rejected" | "cancelled_by_host" | "cancelled_by_user";
    reason?: ReservationDeclineReason;
    message?: string;
  }) => Promise<ReservationRequest>;
  isPending: Ref<boolean>;
} {
  const trpc = useTrpc();
  const queryClient = useQueryClient();
  const shop = useShop();

  const mutation = useMutation({
    mutationFn: (input: {
      request_id: string;
      status: "rejected" | "cancelled_by_host" | "cancelled_by_user";
      reason?: ReservationDeclineReason;
      message?: string;
    }) =>
      trpc.reservations.updateRequestStatus.mutate({
        request_id: input.request_id,
        status: input.status,
        reason: input.reason,
        message: input.message,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({
        queryKey: reservationRequestKeys.lists(),
      });
      const snapshots = queryClient.getQueriesData<InfiniteData<PageData>>({
        queryKey: reservationRequestKeys.lists(),
      });
      queryClient.setQueriesData<InfiniteData<PageData>>(
        { queryKey: reservationRequestKeys.lists() },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  data: page.data.filter((r) => r.id !== input.request_id),
                })),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_err, _input, context) => {
      context?.snapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSettled: async (_data, _err, input) => {
      await invalidateAfterRejectRequest(queryClient, {
        requestId: input.request_id,
      });
    },
    onSuccess: () => {
      void shop.refreshData();
      options?.onSuccess?.();
    },
  });

  return {
    mutate: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending as Ref<boolean>,
  };
}
